import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ChevronRight, Gift, CreditCard, Sparkles, Clock, 
  ShoppingBag, Check, Loader2, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GiftCardPage() {
  const [options, setOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('montants');
  
  // Selection states
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedPrestation, setSelectedPrestation] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    receiver_name: '',
    sender_name: '',
    personal_message: ''
  });
  
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/gift-card/options`);
      setOptions(response.data);
    } catch (err) {
      console.error('Error fetching gift card options:', err);
      toast.error('Erreur lors du chargement des options');
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentAmount = () => {
    if (activeTab === 'montants' && selectedPreset) {
      return selectedPreset.amount;
    }
    if (activeTab === 'personnalise' && customAmount) {
      return parseFloat(customAmount);
    }
    if (activeTab === 'prestations' && selectedPrestation) {
      return selectedPrestation.price;
    }
    return 0;
  };

  const isValidSelection = () => {
    const amount = getCurrentAmount();
    if (amount <= 0) return false;
    
    if (activeTab === 'personnalise' && options) {
      const numAmount = parseFloat(customAmount);
      if (numAmount < options.min_custom_amount || numAmount > options.max_custom_amount) {
        return false;
      }
    }
    
    return true;
  };

  const handleAddToCart = () => {
    if (!isValidSelection()) {
      toast.error('Veuillez sélectionner un montant valide');
      return;
    }

    const amount = getCurrentAmount();
    let giftType = 'preset_amount';
    let prestationId = null;
    let prestationName = null;

    if (activeTab === 'personnalise') {
      giftType = 'custom_amount';
    } else if (activeTab === 'prestations' && selectedPrestation) {
      giftType = 'prestation';
      prestationId = selectedPrestation.id;
      prestationName = selectedPrestation.name;
    }

    const cartItem = {
      type: 'gift_card',
      gift_type: giftType,
      amount: amount,
      totalPrice: amount,
      prestation_id: prestationId,
      prestation_name: prestationName,
      receiver_name: formData.receiver_name || null,
      sender_name: formData.sender_name || null,
      personal_message: formData.personal_message || null
    };

    addItem(cartItem);
    toast.success('Carte cadeau ajoutée au panier !');
    
    // Reset selection
    setSelectedPreset(null);
    setCustomAmount('');
    setSelectedPrestation(null);
    setFormData({ receiver_name: '', sender_name: '', personal_message: '' });
  };

  if (isLoading) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden"
        style={{ 
          background: 'linear-gradient(135deg, #f8f4f1 0%, #f4ece9 50%, #efe7e4 100%)'
        }}
        data-testid="giftcard-hero"
      >
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="gift-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                <circle cx="30" cy="30" r="1" fill="#d4a5a5" opacity="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#gift-pattern)" />
          </svg>
        </div>
        
        <div className="relative z-10 text-center px-6 py-20 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 text-xs tracking-[2px] uppercase text-[#d4a5a5] font-medium mb-6 px-4 py-2 border border-[#d4a5a5]/30 bg-white/50 backdrop-blur-sm"
          >
            <Gift size={14} />
            Boutique
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-6xl font-light text-[#2c2c2c] mb-6"
          >
            Cartes Cadeaux
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[#6b6b6b] text-base md:text-lg max-w-xl mx-auto"
          >
            Offrez un moment d'exception à vos proches. 
            Une carte cadeau pour des soins de beauté et de bien-être.
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)] transition-colors">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Cartes Cadeaux</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16 px-6 bg-white" data-testid="giftcard-content">
        <div className="max-w-[1000px] mx-auto">
          {/* Section Title */}
          <FadeIn className="text-center mb-12">
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#2c2c2c] mb-3">
              Choisissez votre cadeau
            </h2>
            <p className="text-[#6b6b6b] text-sm">
              Sélectionnez le type de carte cadeau que vous souhaitez offrir
            </p>
          </FadeIn>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-10 bg-[#f8f4f1] p-1 rounded-none h-auto">
              <TabsTrigger 
                value="montants" 
                className="py-4 text-xs uppercase tracking-[1px] data-[state=active]:bg-white data-[state=active]:text-[#2c2c2c] data-[state=active]:shadow-sm rounded-none"
                data-testid="tab-montants"
              >
                <CreditCard size={16} className="mr-2" />
                Montants Définis
              </TabsTrigger>
              {options?.allow_custom_amount && (
                <TabsTrigger 
                  value="personnalise" 
                  className="py-4 text-xs uppercase tracking-[1px] data-[state=active]:bg-white data-[state=active]:text-[#2c2c2c] data-[state=active]:shadow-sm rounded-none"
                  data-testid="tab-personnalise"
                >
                  <Sparkles size={16} className="mr-2" />
                  Montant Libre
                </TabsTrigger>
              )}
              <TabsTrigger 
                value="prestations" 
                className="py-4 text-xs uppercase tracking-[1px] data-[state=active]:bg-white data-[state=active]:text-[#2c2c2c] data-[state=active]:shadow-sm rounded-none"
                data-testid="tab-prestations"
              >
                <Heart size={16} className="mr-2" />
                Offrir une Prestation
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Preset Amounts */}
            <TabsContent value="montants" className="mt-0">
              <FadeIn>
                <div className="text-center mb-8">
                  <p className="text-[#6b6b6b] text-sm">
                    Choisissez parmi nos montants prédéfinis
                  </p>
                </div>

                {options?.preset_amounts?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                    {options.preset_amounts.map((preset) => (
                      <motion.div
                        key={preset.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedPreset(preset)}
                        className={`cursor-pointer p-8 border-2 transition-all duration-300 text-center ${
                          selectedPreset?.id === preset.id
                            ? 'border-[#d4a5a5] bg-[#f8f4f1]'
                            : 'border-[#e8e8e8] bg-white hover:border-[#d4a5a5]/50'
                        }`}
                        data-testid={`preset-${preset.amount}`}
                      >
                        <div className="font-['Cormorant_Garamond'] text-4xl font-light text-[#2c2c2c] mb-3">
                          {preset.amount}€
                        </div>
                        {preset.description && (
                          <p className="text-sm text-[#6b6b6b]">{preset.description}</p>
                        )}
                        {selectedPreset?.id === preset.id && (
                          <div className="mt-4 flex items-center justify-center gap-2 text-[#d4a5a5]">
                            <Check size={16} />
                            <span className="text-xs uppercase tracking-wider">Sélectionné</span>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#6b6b6b]">
                    Aucun montant prédéfini disponible pour le moment.
                  </div>
                )}
              </FadeIn>
            </TabsContent>

            {/* Tab 2: Custom Amount */}
            <TabsContent value="personnalise" className="mt-0">
              <FadeIn>
                <div className="max-w-md mx-auto">
                  <div className="text-center mb-8">
                    <p className="text-[#6b6b6b] text-sm">
                      Définissez le montant de votre choix
                    </p>
                  </div>

                  <div className="bg-[#f8f4f1] p-8 border border-[#e8e8e8]">
                    <Label className="block text-xs tracking-[1px] uppercase text-[#2c2c2c] mb-3 font-medium">
                      Montant souhaité
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        min={options?.min_custom_amount || 10}
                        max={options?.max_custom_amount || 500}
                        placeholder={`${options?.min_custom_amount || 10}`}
                        className="text-2xl font-['Cormorant_Garamond'] text-center py-6 pr-12 rounded-none border-[#e8e8e8] focus:border-[#d4a5a5]"
                        data-testid="custom-amount-input"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-[#6b6b6b]">€</span>
                    </div>
                    <p className="text-xs text-[#6b6b6b] text-center mt-3">
                      Montant minimum : {options?.min_custom_amount || 10}€ — Maximum : {options?.max_custom_amount || 500}€
                    </p>
                  </div>
                </div>
              </FadeIn>
            </TabsContent>

            {/* Tab 3: Prestations */}
            <TabsContent value="prestations" className="mt-0">
              <FadeIn>
                <div className="text-center mb-8">
                  <p className="text-[#6b6b6b] text-sm">
                    Offrez directement une prestation de votre choix
                  </p>
                </div>

                {options?.eligible_prestations?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                    {options.eligible_prestations.map((prestation) => (
                      <motion.div
                        key={prestation.id}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => setSelectedPrestation(prestation)}
                        className={`cursor-pointer p-6 border-2 transition-all duration-300 ${
                          selectedPrestation?.id === prestation.id
                            ? 'border-[#d4a5a5] bg-[#f8f4f1]'
                            : 'border-[#e8e8e8] bg-white hover:border-[#d4a5a5]/50'
                        }`}
                        data-testid={`prestation-${prestation.id}`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-['Cormorant_Garamond'] text-xl text-[#2c2c2c]">
                              {prestation.name}
                            </h3>
                            {prestation.duration && (
                              <div className="flex items-center gap-1 text-xs text-[#6b6b6b] mt-1">
                                <Clock size={12} />
                                {prestation.duration}
                              </div>
                            )}
                          </div>
                          <div className="font-['Cormorant_Garamond'] text-2xl text-[#d4a5a5]">
                            {prestation.price}€
                          </div>
                        </div>
                        {prestation.description && (
                          <p className="text-sm text-[#6b6b6b] line-clamp-2">
                            {prestation.description}
                          </p>
                        )}
                        {selectedPrestation?.id === prestation.id && (
                          <div className="mt-4 flex items-center gap-2 text-[#d4a5a5]">
                            <Check size={16} />
                            <span className="text-xs uppercase tracking-wider">Sélectionné</span>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#6b6b6b]">
                    Aucune prestation disponible en carte cadeau pour le moment.
                  </div>
                )}
              </FadeIn>
            </TabsContent>
          </Tabs>

          {/* Personalization Form */}
          <AnimatePresence>
            {isValidSelection() && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-10 border-t border-[#e8e8e8] pt-10"
              >
                <FadeIn>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl text-center text-[#2c2c2c] mb-6">
                    Personnalisez votre carte (optionnel)
                  </h3>
                  
                  <div className="max-w-lg mx-auto space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="block text-xs tracking-[1px] uppercase text-[#2c2c2c] mb-2">
                          Nom du bénéficiaire
                        </Label>
                        <Input
                          value={formData.receiver_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, receiver_name: e.target.value }))}
                          placeholder="Prénom"
                          className="rounded-none border-[#e8e8e8] focus:border-[#d4a5a5]"
                          data-testid="receiver-name"
                        />
                      </div>
                      <div>
                        <Label className="block text-xs tracking-[1px] uppercase text-[#2c2c2c] mb-2">
                          De la part de
                        </Label>
                        <Input
                          value={formData.sender_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, sender_name: e.target.value }))}
                          placeholder="Votre prénom"
                          className="rounded-none border-[#e8e8e8] focus:border-[#d4a5a5]"
                          data-testid="sender-name"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="block text-xs tracking-[1px] uppercase text-[#2c2c2c] mb-2">
                        Message personnel
                      </Label>
                      <Textarea
                        value={formData.personal_message}
                        onChange={(e) => setFormData(prev => ({ ...prev, personal_message: e.target.value }))}
                        placeholder="Écrivez un message pour accompagner votre cadeau..."
                        className="min-h-[100px] rounded-none border-[#e8e8e8] focus:border-[#d4a5a5]"
                        data-testid="personal-message"
                      />
                    </div>
                  </div>
                </FadeIn>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Add to Cart Section */}
          <div className="mt-10 p-8 bg-[#f8f4f1] border border-[#e8e8e8]">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <p className="text-xs uppercase tracking-[1px] text-[#6b6b6b] mb-1">
                  Total de votre carte cadeau
                </p>
                <div className="font-['Cormorant_Garamond'] text-4xl text-[#2c2c2c]">
                  {getCurrentAmount().toFixed(2)}€
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleAddToCart}
                  disabled={!isValidSelection()}
                  className="px-10 py-6 bg-[#2c2c2c] text-white text-xs uppercase tracking-[2px] font-medium hover:bg-[#1a1a1a] disabled:opacity-50 disabled:cursor-not-allowed rounded-none"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag size={16} className="mr-2" />
                  Ajouter au panier
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/panier')}
                  className="px-8 py-6 border-[#2c2c2c] text-[#2c2c2c] text-xs uppercase tracking-[2px] hover:bg-[#2c2c2c] hover:text-white rounded-none"
                  data-testid="view-cart-btn"
                >
                  Voir le panier
                </Button>
              </div>
            </div>
          </div>

          {/* Info Section */}
          <FadeIn delay={0.2}>
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-[#f8f4f1] rounded-full">
                  <Gift size={24} className="text-[#d4a5a5]" />
                </div>
                <h4 className="font-['Cormorant_Garamond'] text-lg text-[#2c2c2c] mb-2">
                  Livraison instantanée
                </h4>
                <p className="text-sm text-[#6b6b6b]">
                  Recevez votre carte cadeau par email immédiatement après l'achat
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-[#f8f4f1] rounded-full">
                  <Clock size={24} className="text-[#d4a5a5]" />
                </div>
                <h4 className="font-['Cormorant_Garamond'] text-lg text-[#2c2c2c] mb-2">
                  Validité 1 an
                </h4>
                <p className="text-sm text-[#6b6b6b]">
                  Votre carte cadeau est valable pendant 12 mois à compter de la date d'achat
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-[#f8f4f1] rounded-full">
                  <Sparkles size={24} className="text-[#d4a5a5]" />
                </div>
                <h4 className="font-['Cormorant_Garamond'] text-lg text-[#2c2c2c] mb-2">
                  Toutes prestations
                </h4>
                <p className="text-sm text-[#6b6b6b]">
                  Utilisable sur l'ensemble de nos soins : onglerie, pédicure et bien-être
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </PageTransition>
  );
}
